import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, DollarSign, BarChart, Loader2 } from 'lucide-react';

const HardcodedResultsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const opportunities = [
    {
      brand: 'Apple',
      type: 'Product Placement',
      value: 600000,
      confidence: 'High',
      details: 'iPhone prominently displayed in a scene.',
    },
    {
      brand: 'Nike',
      type: 'Visible Logo',
      value: 250000,
      confidence: 'Medium',
      details: 'Character wearing Nike shoes.',
    },
    {
        brand: 'Coca-Cola',
        type: 'Brand Mention',
        value: 150000,
        confidence: 'High',
        details: 'Character orders a "Coke".',
    },
    {
        brand: 'Ford',
        type: 'Vehicle Placement',
        value: 200000,
        confidence: 'High',
        details: 'Character drives a Ford Mustang.',
    },
    {
        brand: 'Starbucks',
        type: 'Brand Integration',
        value: 100000,
        confidence: 'Medium',
        details: 'Characters meet at a Starbucks.',
    },
    {
        brand: 'Sony',
        type: 'Product Placement',
        value: 100000,
        confidence: 'Low',
        details: 'A Sony TV is visible in the background.',
    },
  ];

  const totalValue = opportunities.reduce((sum, opp) => sum + opp.value, 0);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-yellow-50">
        <div className="text-center">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" />
          <h2 className="mt-4 text-xl font-semibold text-slate-900">Analyzing Script...</h2>
          <p className="mt-2 text-sm text-slate-600">This may take a few moments.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-yellow-50 p-6">
      <div className="mx-auto max-w-5xl">
        <Button variant="ghost" onClick={() => navigate('/projects/new')} className="mb-6 text-slate-900">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Create Project
        </Button>

        <header className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900">Script Analysis Report</h1>
          <p className="text-lg text-slate-600 mt-2">
            Hardcoded analysis for "Project X - Sample Script"
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white shadow-md">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-900">Total Opportunities</CardTitle>
                    <FileText className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                    <div className="text-2xl font-bold text-slate-900">{opportunities.length}</div>
                    <p className="text-xs text-muted-foreground">identified in the script</p>
                </CardContent>
            </Card>
            <Card className="bg-white shadow-md">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-900">Estimated Total Value</CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                    <div className="text-2xl font-bold text-slate-900">${totalValue.toLocaleString()}</div>
                    <p className="text-xs text-muted-foreground">based on current selections</p>
                </CardContent>
            </Card>
            <Card className="bg-white shadow-md">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-900">Confidence Level</CardTitle>
                    <BarChart className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                    <div className="text-2xl font-bold text-slate-900">High</div>
                    <p className="text-xs text-muted-foreground">overall match confidence</p>
                </CardContent>
            </Card>
        </div>

        <Card className="bg-white shadow-md">
          <CardHeader>
            <CardTitle className="text-slate-900">Identified Integration Opportunities</CardTitle>
            <CardDescription>The following potential brand integrations have been identified in the script.</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-slate-900">Brand</TableHead>
                  <TableHead className="text-slate-900">Integration Type</TableHead>
                  <TableHead className="text-slate-900">Details</TableHead>
                  <TableHead className="text-slate-900">Confidence</TableHead>
                  <TableHead className="text-right text-slate-900">Estimated Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {opportunities.map((opp, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium text-slate-900">{opp.brand}</TableCell>
                    <TableCell className="text-slate-700">{opp.type}</TableCell>
                    <TableCell className="text-sm text-slate-600">{opp.details}</TableCell>
                    <TableCell className="text-slate-700">{opp.confidence}</TableCell>
                    <TableCell className="text-right text-slate-900">${opp.value.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HardcodedResultsPage;